// src/components/Admin/ZeroOutBalanceModal.js
"use client";

import { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import api from "../../services/api";
import Swal from "sweetalert2";
import MoneyInput from "../../components/Custombb/MoneyInput"; // فرض می‌کنیم این کامپوننت را دارید

// تعریف Enum ها برای وضوح بیشتر
const TransactionSource = {
  WALLET: "wallet",
  CREDIT: "credit",
};

const ZeroOutBalanceModal = ({
  show,
  onHide,
  userId,          // برای تسویه تکی
  categoryId,      // برای تسویه دسته کاربران
  merchantCategoryId, // برای تسویه کیف دسته بندی تجاری
  targetType = "USER", // "USER" | "CATEGORY" | "GENERAL_WALLET" | "PRODUCT_CATEGORY_WALLET"
  onSuccess,
}) => {
  const [selectedUserId, setSelectedUserId] = useState(userId || null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId || null);
  const [selectedMerchantCategoryId, setSelectedMerchantCategoryId] = useState(merchantCategoryId || null);
  const [source, setSource] = useState(TransactionSource.CREDIT); // پیش‌فرض کیف پول
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const [userOptions, setUserOptions] = useState([]); // برای فیلتر کردن کاربران
  const [categoryOptions, setCategoryOptions] = useState([]); // برای دسته‌های کاربران
  const [productCategoryOptions, setProductCategoryOptions] = useState([]); // برای دسته‌های محصول

  // تعیین نوع عملیات بر اساس targetType
  const isUserTarget = targetType === "USER";
  const isCategoryTarget = targetType === "CATEGORY";
  const isGeneralWalletTarget = targetType === "GENERAL_WALLET";
  const isProductCategoryWalletTarget = targetType === "PRODUCT_CATEGORY_WALLET";

  // گرفتن مقادیر پیش‌فرض اگر از بیرون پاس داده شده باشند
  useEffect(() => {
    if (userId) setSelectedUserId(userId);
    if (categoryId) setSelectedCategoryId(categoryId);
    if (merchantCategoryId) setSelectedMerchantCategoryId(merchantCategoryId);
  }, [userId, categoryId, merchantCategoryId]);

  // Fetch dropdown options
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isUserTarget || isGeneralWalletTarget) {
          // در حالت کلی یا تسویه کاربر تکی، لیست همه کاربران یا دسته‌بندی‌ها را می‌گیریم
          // ممکن است بخواهید این را با پارامترهای بیشتر فیلتر کنید
          const userRes = await api.get('/users?isActive=true'); // مثال: گرفتن لیست کاربران
          setUserOptions(userRes.data);
        }
        if (isCategoryTarget || isUserTarget || isGeneralWalletTarget) {
          const categoryRes = await api.get('/categories'); // مثال: گرفتن لیست دسته‌بندی کاربران
          setCategoryOptions(categoryRes.data);
        }
        if (isProductCategoryWalletTarget || isCategoryTarget || isUserTarget || isGeneralWalletTarget) {
          const productCatRes = await api.get('/merchant-categories?isActive=true'); // مثال: گرفتن لیست دسته‌بندی محصولات
          setProductCategoryOptions(productCatRes.data);
        }
      } catch (err) {
        console.error('Failed to fetch dropdown options', err);
        Swal.fire({ icon: "error", title: "خطا", text: "امکان بارگذاری گزینه‌ها وجود ندارد." });
      }
    };
    if (show) fetchData();
  }, [show, isUserTarget, isCategoryTarget, isGeneralWalletTarget, isProductCategoryWalletTarget]);


  const handleSubmit = async () => {
    // اعتبارسنجی های لازم
    if (!source) {
      Swal.fire({ icon: "error", title: "خطا", text: "نوع منبع (کیف پول/اعتبار) را انتخاب کنید." });
      return;
    }

    let payload = {
      source,
      desc: description,
    };

    // تعیین پارامترها بر اساس targetType
    if (isUserTarget) {
      if (!selectedUserId) {
        Swal.fire({ icon: "error", title: "خطا", text: "لطفاً کاربر مورد نظر را انتخاب کنید." });
        return;
      }
      payload.userId = selectedUserId;
    } else if (isCategoryTarget) {
      if (!selectedCategoryId) {
        Swal.fire({ icon: "error", title: "خطا", text: "لطفاً دسته کاربران مورد نظر را انتخاب کنید." });
        return;
      }
      payload.categoryId = selectedCategoryId;
    } else if (isProductCategoryWalletTarget) {
      if (!selectedMerchantCategoryId) {
        Swal.fire({ icon: "error", title: "خطا", text: "لطفاً دسته محصول مورد نظر را انتخاب کنید." });
        return;
      }
      payload.merchantCategoryId = selectedMerchantCategoryId;
    } else if (isGeneralWalletTarget) {
      // برای تسویه عمومی، هیچ userId, categoryId, merchantCategoryId لازم نیست
      // فقط source و desc کافی است.
    }

    // اگر merchantCategoryId در حالت تکی یا دسته‌ای هم خواسته شد، اضافه شود
    if (isUserTarget || isCategoryTarget) {
        payload.merchantCategoryId = selectedMerchantCategoryId || undefined; // اضافه کردن اختیاری
    }


    setLoading(true);
    try {
      // ارسال درخواست به API بک‌اند
      const response = await api.post('/admin/zero-out-balance', payload);

      Swal.fire({
        icon: "success",
        title: "موفقیت",
        text: response.data.message || "عملیات تسویه با موفقیت انجام شد.",
        timer: 3000,
        showConfirmButton: false,
      });
      onSuccess?.(); // فراخوانی تابع callback پس از موفقیت
      onHide(); // بستن مودال
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "خطا",
        text: err.response?.data?.message || "خطا در انجام عملیات تسویه.",
      });
    } finally {
      setLoading(false);
    }
  };

  // رندر کردن فیلدهای مربوط به هر targetType
  const renderTargetSpecificFields = () => {
    switch (targetType) {
      case "USER":
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>انتخاب کاربر</Form.Label>
              <Form.Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(Number(e.target.value))}
                required
              >
                <option value="" disabled>-- کاربر --</option>
                {userOptions.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.username})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>دسته‌بندی محصول (اختیاری)</Form.Label>
                <Form.Select
                    value={selectedMerchantCategoryId || ""} // اگر null بود، "" نمایش داده شود
                    onChange={(e) => setSelectedMerchantCategoryId(e.target.value ? Number(e.target.value) : null)}
                >
                    <option value="">همه دسته‌بندی‌های محصول</option>
                    {productCategoryOptions.map((pc) => (
                    <option key={pc.id} value={pc.id}>{pc.title}</option>
                    ))}
                </Form.Select>
            </Form.Group>
          </>
        );

      case "CATEGORY":
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>انتخاب دسته کاربران</Form.Label>
              <Form.Select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                required
              >
                <option value="" disabled>-- دسته کاربران --</option>
                {categoryOptions.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
             <Form.Group className="mb-3">
                <Form.Label>دسته‌بندی محصول (اختیاری)</Form.Label>
                <Form.Select
                    value={selectedMerchantCategoryId || ""}
                    onChange={(e) => setSelectedMerchantCategoryId(e.target.value ? Number(e.target.value) : null)}
                >
                    <option value="">همه دسته‌بندی‌های محصول</option>
                    {productCategoryOptions.map((pc) => (
                    <option key={pc.id} value={pc.id}>{pc.title}</option>
                    ))}
                </Form.Select>
            </Form.Group>
          </>
        );

      case "PRODUCT_CATEGORY_WALLET":
        return (
          <Form.Group className="mb-3">
            <Form.Label>انتخاب دسته محصول</Form.Label>
            <Form.Select
              value={selectedMerchantCategoryId}
              onChange={(e) => setSelectedMerchantCategoryId(Number(e.target.value))}
              required
            >
              <option value="" disabled>-- دسته محصول --</option>
              {productCategoryOptions.map((pc) => (
                <option key={pc.id} value={pc.id}>
                  {pc.title}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        );

      case "GENERAL_WALLET":
        return (
            <Form.Group className="mb-3">
              <Form.Label>عملیات برای همه کاربران (کیف عمومی)</Form.Label>
              <p className="text-muted">
                با انتخاب این گزینه، موجودی کیف پول عمومی همه کاربران تسویه خواهد شد.
              </p>
            </Form.Group>
        );

      default:
        return null;
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isUserTarget && "تسویه حساب کاربر"}
          {isCategoryTarget && "تسویه حساب دسته کاربران"}
          {isProductCategoryWalletTarget && "تسویه کیف دسته محصول"}
          {isGeneralWalletTarget && "تسویه کیف عمومی همه کاربران"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {renderTargetSpecificFields()}

          <Form.Group className="mb-3">
            <Form.Label>منبع</Form.Label>
            <Form.Select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              required
            >
              <option value={TransactionSource.CREDIT}>اعتبار</option>
              <option value={TransactionSource.WALLET}>کیف پول</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>توضیحات (اختیاری)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="توضیحات عملیات تسویه..."
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
	        <Button variant="secondary" onClick={onHide}>
	          انصراف
	        </Button>
	        <Button
	          variant="danger" // برای تسویه، دکمه قرمز بهتر است
	          onClick={handleSubmit}
	          disabled={loading}
	        >
	          {loading ? "در حال تسویه..." : "تسویه حساب"}
	        </Button>
	      </Modal.Footer>
	    </Modal>
	  );
};

export default ZeroOutBalanceModal;
